/**
 * AssistedOnboardingCard — the "Virtual Assisted Onboarding" card that
 * lives inside the Settings → Concierge & Guidance sub-tab.
 *
 * Extracted from SettingsTab.js in Session 265 to keep the SettingsTab
 * component under the 200-line frontend function-length gate. The card
 * owns its own "Manage Sessions" toggle state — nothing else in Settings
 * needs it, so keeping the state local avoids a prop-drill.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, Headphones, Calendar } from 'lucide-react';
import AssistedOnboardingSessions from '../AssistedOnboardingSessions';

const AssistedOnboardingCard = ({ onQuickSetup, onDeepOnboarding, onJoinSession }) => {
  const [showManageSessions, setShowManageSessions] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6" data-testid="assisted-onboarding-section">
      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">Virtual Assisted Onboarding</h3>
      <p className="text-sm text-slate-500 mb-2">
        Get personalized guidance from a MyInvestFolio specialist via a private co-viewing session.
      </p>
      <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-5 flex items-start gap-2" data-testid="onboarding-privacy-notice">
        <Eye className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <span>Your investment portfolio is masked during these sessions. Our support staff only see field names and form structure. They do not see your asset names and actual values.</span>
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-slate-800">Quick Virtual Setup Session</h4>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            A complimentary 10-15 minute call to help you take your first steps on MIF — adding investments, navigating features, uploading data.
          </p>
          <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={onQuickSetup}
            data-testid="settings-quick-setup-btn">
            <Headphones className="w-3.5 h-3.5 mr-1.5" /> Book Quick Setup Session
          </Button>
        </div>

        <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/50">
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="w-5 h-5 text-emerald-600" />
            <h4 className="font-semibold text-slate-800">Private Virtual Deep Onboarding</h4>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            A 60-minute deep-dive session for serious portfolio users. Tell us what you need, and we&apos;ll review your request to arrange a dedicated session.
          </p>
          <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={onDeepOnboarding}
            data-testid="settings-deep-onboarding-btn">
            <Headphones className="w-3.5 h-3.5 mr-1.5" /> Request Deep Onboarding
          </Button>
        </div>
      </div>

      <div className="mt-5 border-t border-slate-200 pt-4">
        <Button
          variant={showManageSessions ? "default" : "outline-solid"}
          className={showManageSessions ? "bg-slate-800 hover:bg-slate-700 text-white" : ""}
          onClick={() => setShowManageSessions(!showManageSessions)}
          data-testid="manage-sessions-btn">
          <Calendar className="w-4 h-4 mr-1.5" />
          {showManageSessions ? 'Hide Sessions' : 'Manage Sessions'}
        </Button>
        {showManageSessions && (
          <div className="mt-4">
            <AssistedOnboardingSessions onJoinSession={onJoinSession} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistedOnboardingCard;
